import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { reportUnexpectedError } from '../../../lib/reportError'
import { isDeviceForbiddenError } from '../../../lib/deviceAuthError'

export interface RemoveStampsInput {
  memberId: string
  pin: string
  removalCount: number
  reason: string
  note: string
}

export interface RemoveStampsResult {
  newStampTotal: number
  stampTarget: number
}

interface RemoveStampsResponse {
  success?: boolean
  error?: string
  newStampTotal?: number
  stampTarget?: number
}

export class InvalidManagerPinError extends Error {
  constructor() {
    super('invalid_pin')
  }
}

async function removeStamps(input: RemoveStampsInput): Promise<RemoveStampsResult> {
  const { data, error } = await supabase.functions.invoke<RemoveStampsResponse>('correct-stamps', {
    body: {
      memberId:     input.memberId,
      pin:          input.pin,
      removalCount: input.removalCount,
      reason:       input.reason,
      note:         input.note,
    },
  })

  if (data?.error === 'invalid_pin') throw new InvalidManagerPinError()
  if (error || !data?.success) {
    const failure = error ?? new Error('remove_stamps_failed')
    if (!isDeviceForbiddenError(failure)) reportUnexpectedError(failure)
    throw failure
  }

  return {
    newStampTotal: data.newStampTotal!,
    stampTarget:   data.stampTarget!,
  }
}

export function useRemoveStamps() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: removeStamps,
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['member-stamp-data', variables.memberId] })
    },
  })
}